import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";
import { useStoreState } from "ezStore";

import { Filter, Table } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";

import EventBusyIcon from "@mui/icons-material/EventBusy";

import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";

import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission, usePermission } from "imports/Utils";

import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import { Call } from "view/planning/container/ContainerPlanning";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import useCargoTable from "view/cargos/hooks/useCargoTable";

import { DossierLink, LocationLink } from "utils/LinkUtils";

import { ChildPropsFromPagination } from "types/commons";
import { gql } from "__generated__/gql";
import { ContainerPlanningCargosQuery } from "__generated__/graphql";
import { SortDirection, CallType, StatusType } from "__generated__/enums";
import { UnplanContainersFromVoyageDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";

export type ContainerFromQuery = ChildPropsFromPagination<ContainerPlanningCargosQuery, "cargos">;

const ContainerPlanningTable = ({
	onStartDragging,
	onFinishDragging,
	conditions,
	refetch,
	updateUnplannedTableKey,
}: {
	onStartDragging: (rows: ContainerFromQuery[]) => void;
	onFinishDragging: () => void;
	conditions: {
		isUnplanned: boolean;
		loadingOrDischarging: CallType;
		call?: Call
	}
	refetch: () => void;
	updateUnplannedTableKey?: number;
}) => {

	const session = useStoreState(state => state.session);
	const client = useApolloClient();
	const { isReadOnly } = usePermission();
	const confirm = useConfirm();

	const [ unplanContainersFromVoyage ] = useMutation(UnplanContainersFromVoyageDocument);

	const { getSearchInput, filterFields } = useCargoTable();

	const { tableProps, refetch: tableRefetch, filterProps } = useBackendTable({
		id: TableId.Containers,
		selectSettings: {
			isFullSelectEnabled: false,
			selectionEnabled: conditions.isUnplanned,
		},
		onChange: async(pagination, sort, filter, callback) => {

			const optionalFilters: Record<string, unknown> = {
				isUnplanned: conditions.isUnplanned,
				plannableOnShip: true,
			};

			if (conditions.call && conditions.loadingOrDischarging === CallType.Loading && conditions.isUnplanned) {
				optionalFilters.hasLoadingCall = false;
				optionalFilters.loadingLocations = [ conditions.call.location.id ];

				const calls = conditions.call.voyage.calls;
				const startIndex = calls.findIndex(c => c.id === conditions.call?.id);
				const nextCalls = calls.slice(startIndex + 1);

				optionalFilters.dischargingLocations = nextCalls.length > 0 ? nextCalls.map(it => it.location.id) : undefined;
			}

			if (conditions.call && conditions.loadingOrDischarging === CallType.Discharging && conditions.isUnplanned) {
				optionalFilters.hasDischargingCall = false;
				optionalFilters.dischargingLocations = [ conditions.call.location.id ];

				const calls = conditions.call.voyage.calls;
				const endIndex = calls.findIndex(c => c.id === conditions.call?.id);
				const prevCalls = calls.slice(0, endIndex);

				optionalFilters.loadingLocations = prevCalls.length > 0 ? prevCalls.map(it => it.location.id) : undefined;
			}

			if (conditions.call && conditions.loadingOrDischarging === CallType.Loading && !conditions.isUnplanned) {
				optionalFilters.hasLoadingCall = true;
				optionalFilters.loadingCalls = [ conditions.call.id ];
			}

			if (conditions.call && conditions.loadingOrDischarging === CallType.Discharging && !conditions.isUnplanned) {
				optionalFilters.hasDischargingCall = true;
				optionalFilters.dischargingCalls = [ conditions.call.id ];
			}

			try {
				const result = await client.query({
					query: query,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "created",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							...getSearchInput(filter),
							...optionalFilters,
						},
					},
				});

				callback({
					content: result.data.cargos.content,
					totalResults: result.data.cargos.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const unplan = (row: ContainerFromQuery) => {
		if (row.firstLoadingAction?.call?.voyage.id) {
			unplanContainersFromVoyage({
				variables: {
					voyageId: row.firstLoadingAction?.call?.voyage.id,
					cargoIds: [ row.id ],
				},
			}).then(() => {
				refetch();
				tableRefetch(0);
			});
		}
	};

	usePropertyChangeEffect(() => {
		tableRefetch(0);
	}, [ conditions.call?.id, conditions.loadingOrDischarging, updateUnplannedTableKey ]);

	return (
		<Table
			{...tableProps}
			filterComponent={() => (
				<Filter
					{...filterProps}
					fields={[
						filterFields.Relations,
						filterFields.CharterNumber,
						filterFields.CustomerReference,
						filterFields.Ships,
						filterFields.Voyages,
						filterFields.LoadingLocations,
						filterFields.DischargingLocations,
						filterFields.CurrentStatuses,
						filterFields.ContainerNumber,
						filterFields.ContainerType,
						filterFields.Empty,
						filterFields.LoadingReference,
						filterFields.DischargingReference,
						filterFields.ConnectingVisitLoadingCallReferenceNumber,
						filterFields.ConnectingVisitDischargingCallReferenceNumber,
						filterFields.HasDangerousGoods,
					]} />
			)}
			dragAndDropEnabled={!isReadOnly && conditions.isUnplanned && !!conditions.call}
			dragDropSettings={{
				onDragStart: (e, row) => {
					const currentSelection = (tableProps.tableRef.current?.getSelection?.().selection ?? []) as ContainerFromQuery[];
					const containers = currentSelection.length > 0 ? currentSelection : [ row ];
					e.dataTransfer.setData("container-planning-containers", JSON.stringify(containers));
					onStartDragging(containers);
				},
				onDragEnd: () => {
					onFinishDragging();
				},
				renderPreview: (row) => {
					const currentSelection = (tableProps.tableRef.current?.getSelection?.().selection ?? []) as ContainerFromQuery[];
					const containers = currentSelection.length > 0 ? currentSelection : [ row ];
					return (
						<div className="cargo-drag">
							<span>{intl.formatMessage({ id: "X_CARGOS_SELECTED" }).replace("_X_", containers.length.toString())}</span>
						</div>
					);
				},
			}}
			columns={[ {
				id: "currentStatuses",
				title: intl.formatMessage({ id: "STATUS" }),
				render: (row) => {
					return (
						<StatusChanger
							session={session}
							entity={row}
							statusType={StatusType.Cargo}
							refetch={refetch} />
					);
				},
				sortable: true,
				sortKey: "lastStatusChange.status.description",
				disabled: !hasPluginPermission(session, "cargoStatuses"),
				width: 175,
			}, {
				id: "charter.charterNumber",
				title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
				render: (row) => <DossierLink charter={row.charter} />,
				sortable: true,
				width: 130,
			}, {
				id: "containerNumber",
				title: intl.formatMessage({ id: "CONTAINER_NUMBER_SHORT" }),
				sortable: true,
				width: 130,
			}, {
				id: "containerType",
				title: intl.formatMessage({ id: "CARGO_CONTAINER_TYPE" }),
				render: (row) => DataFormat.containerType(row.containerType),
				sortable: true,
				sortKey: "containerType.isoTypeCode",
				width: 150,
			}, {
				id: "isEmpty",
				title: intl.formatMessage({ id: "CARGO_FULL_OR_EMPTY" }),
				render: (row) => row.empty ? intl.formatMessage({ id: "CARGO_EMPTY" }) : intl.formatMessage({ id: "CARGO_FULL" }),
				sortable: true,
				sortKey: "empty",
			}, {
				id: "hasDangerousGoods",
				title: intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" }),
				render: row => DataFormat.boolean(row.hasDangerousGoods),
			}, {
				id: "loadingLocation",
				title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
				render: (row) => <LocationLink location={row.firstLoadingAction?.location} />,
				sortable: true,
				sortKey: "firstLoadingAction.location.description",
				width: 150,
			}, {
				id: "dischargingLocation",
				title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
				render: (row) => <LocationLink location={row.lastDischargingAction?.location} />,
				sortable: true,
				sortKey: "lastDischargingAction.location.description",
				width: 150,
			}, {
				id: "firstLoadingAction.start",
				title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
				render: (row) => <CargoActionDate action={row.firstLoadingAction} />,
				sortable: true,
				width: 250,
				disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingDate"),
			}, {
				id: "lastDischargingAction.end",
				title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
				render: (row) => <CargoActionDate action={row.lastDischargingAction} />,
				sortable: true,
				width: 250,
				disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingDate"),
			} ]}
			rowActions={!conditions.isUnplanned && !!conditions.call ? [ {
				icon: <EventBusyIcon />,
				onClick: (row: ContainerFromQuery) => {
					confirm({
						type: "WARNING",
						title: intl.formatMessage({ id: "CONFIRM_UNPLAN_CONTAINER" }),
					}).then(() => {
						unplan(row);
					});
				},
				tooltip: intl.formatMessage({ id: "UNPLAN" }),
			} ] : undefined}
		/>
	);
};

const query = gql(`#graphql
	query containerPlanningCargos($search: CargoSearch!, $pagination: Pagination) {
		cargos(search: $search, pagination: $pagination) {
			totalElements
			content {
				id
				containerNumber
				charter {
					id
					charterNumber
					type
					debtor {
						id
						name
					}
				}
				lastStatusChange {
					...statusChangeFragment
				}
				firstLoadingAction {
					...CargoActionWithVoyage
				}
				lastDischargingAction {
					...CargoActionWithVoyage
				}
				actions {
					id
					start
					end
					weight
					volume
					type
					location {
						id
						description
					}
				}
				containerType {
					isoTypeCode
					displayCode
				}
				grossWeight
				empty
				hasDangerousGoods
				bookingReference
			}
		}
	}
`);

export default ContainerPlanningTable;
